// 고객사 리포트 (현장앱 구글 시트 수집분) — Field 그룹 독립 화면.
//
// 26-09-11 — 현장앱이 구글 시트에 쌓는 리포트를 콘솔에서 본다.
// 어휘(등급/상태)는 서비스 오더와 **같은 정본 라벨**을 쓴다. 두 화면이
// 나란히 놓이므로 말이 갈리면 안 된다.
//
// 서비스 오더 인박스 안의 '고객 리포트 (접수 원본)' 탭과는 **다른 표**다.
// 그쪽은 CustomerReport(QR 스캔 전제 — robotQrId 필수), 이쪽은
// CustomerSheetReport(시트 수집분). 섞지 않는 이유는 schema.prisma 주석 참조.

import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  kstDateTime,
  SERVICE_ORDER_STATUS_LABELS,
  SERVICE_SEVERITY_LABELS,
  sheetOutcomeLabel,
  type ServiceOrderStatus,
  type ServiceSeverity,
  type CustomerSheetReportDto,
  type ListCustomerSheetReportsQuery,
} from '@bhops/shared';
import { customerSheetReportsApi } from '@/api/customerSheetReports';
import { extractApiError } from '@/api/client';
import { Icon } from '@/components/Icon';
import { kstMonthCursor } from '@/lib/format-date';
import { visitLabel, visitMonthDay } from '@/lib/report-visit';
import { useToast } from '@/components/Toast';
import { useBackdropDismiss } from '@/components/use-backdrop-dismiss';
import { useAuthStore } from '@/stores/auth';
import { canEditAny } from '@/permissions';
import {
  pill,
  SEVERITY_ICON,
  SEVERITY_ORDER,
  SEVERITY_TONE,
  SHEET_STATUS_TONE,
  SHEET_STATUS_TONE_UNKNOWN,
} from './field-tones';
import { StatFilterCard } from './StatFilterCard';
import { FieldReportTabs, fieldReportPage } from './FieldReportTabs';
import { MergeReportPicker } from './MergeReportPicker';

const PAGE_SIZE = 50;

// 이 화면이 쓰는 상태는 4종뿐이다 (오너 결정 26-09-11).
//
// 서비스 오더는 9종(신규·분류완료·배정·진행중·대기·해결·검증·종결·취소)을 쓰지만
// 시트 리포트는 그만큼 잘게 나눌 일이 없다. 수집 기본값도 '대기'다 — 아직 손대지
// 않은 리포트라는 뜻. (서버: CustomerSheetReportsService.ingest)
//
// ⚠ 목록에 4종 밖의 값(과거 데이터 등)이 섞여 있으면 select 가 실제 값을 못 보여준다.
//   statusOptionsFor() 가 그 경우에만 현재 값을 앞에 끼워 넣어 거짓 표시를 막는다.
const SHEET_REPORT_STATUSES: ServiceOrderStatus[] = [
  'BLOCKED', // 대기
  'IN_PROGRESS', // 진행중
  'RESOLVED', // 해결
  'CANCELLED', // 취소
];

/** 상태 드롭다운의 '미해결' 자리 — 상태 값이 아니라 거르기(open)다. */
const OPEN_OPTION = '__open';

function statusOptionsFor(current?: ServiceOrderStatus): ServiceOrderStatus[] {
  if (current && !SHEET_REPORT_STATUSES.includes(current)) {
    return [current, ...SHEET_REPORT_STATUSES];
  }
  return SHEET_REPORT_STATUSES;
}

/**
 * 접수 시간 — 한국 시간 기준 표시(서버는 UTC ISO 로 준다).
 *
 * 표기는 @bhops/shared 의 정본(kstDateTime)을 쓴다. toLocaleString 은 실행
 * 환경의 로캘·타임존을 타서 보는 사람마다 다른 시각이 찍힌다.
 */
function fmtKst(iso: string | null): string {
  return kstDateTime(iso) ?? '–';
}

const th: CSSProperties = {
  background: 'var(--bg-surface)',
  borderBottom: '1px solid var(--border-subtle)',
  padding: '9px 14px',
  textAlign: 'left',
  fontSize: 12,
  fontWeight: 600,
  color: 'var(--text-secondary)',
  whiteSpace: 'nowrap',
};

const td: CSSProperties = {
  padding: '10px 14px',
  fontSize: 13,
  borderBottom: '1px solid var(--border-subtle)',
  verticalAlign: 'middle',
};
// 요약은 적은 줄바꿈대로 보인다(2026-11-02 오너 — 고장 조치 이력 표와 같은 규칙).
const multiline: CSSProperties = { ...td, whiteSpace: 'pre-line' };

const control: CSSProperties = {
  padding: '6px 8px',
  borderRadius: 'var(--r-input)',
  border: '1px solid var(--border-default)',
  fontSize: 13,
  background: 'var(--bg-surface)',
  color: 'var(--fg-default)',
};

/** 최근 n 달(한국 날짜, 이번 달부터) — 'YYYY-MM'. */
function recentMonths(n: number): string[] {
  const { year, month } = kstMonthCursor();
  const out: string[] = [];
  for (let i = 0; i < n; i++) {
    const d = new Date(Date.UTC(year, month - i, 1));
    out.push(`${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`);
  }
  return out;
}

export function CustomerSheetReportsPage() {
  const toast = useToast();
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  // 서비스 오더·리포트 트리아지와 같은 권한 경계(VIEWER 는 읽기만).
  const canEdit = !!user && canEditAny(user.role);

  // 조치 이력에서 넘어올 때 어느 줄을 보여줄지. 주소에 실어 링크로 공유된다.
  // 필드 대시보드에서 넘어올 때는 거르기도 실려 온다 — open=1(미해결) · severity=P1 (2026-10-27).
  const [params] = useSearchParams();
  const highlight = params.get('highlight');
  // 2026-11-02 — 통합 캘린더의 유지보수 일정(콘솔이 올린 리포트)에서 넘어오면 그 번호로 찾아 둔다(q).
  // 칠하기만 하면 그 줄이 첫 쪽에 없을 때 안 보인다.
  const initialSearch = params.get('q')?.trim() || '';

  const [filters, setFilters] = useState<ListCustomerSheetReportsQuery>(() => {
    const sev = params.get('severity');
    return {
      page: 1,
      pageSize: PAGE_SIZE,
      ...(initialSearch ? { search: initialSearch } : {}),
      open: params.get('open') === '1' || undefined,
      severity: SEVERITY_ORDER.includes(sev as ServiceSeverity)
        ? (sev as ServiceSeverity)
        : undefined,
    };
  });
  const [searchDraft, setSearchDraft] = useState(initialSearch);
  const [editing, setEditing] = useState<CustomerSheetReportDto | null>(null);
  // 2026-11-03 — 같은 건 합치기(캘린더가 만든 리포트 → 앱 리포트).
  const [merging, setMerging] = useState<CustomerSheetReportDto | null>(null);

  const q = useQuery({
    queryKey: ['customer-sheet-reports', filters],
    queryFn: () => customerSheetReportsApi.list(filters),
  });

  const counts = q.data?.severityCounts ?? { P1: 0, P2: 0, P3: 0 };
  const items = useMemo(() => q.data?.items ?? [], [q.data]);

  const updateMutation = useMutation({
    mutationFn: (v: {
      id: string;
      status: ServiceOrderStatus;
      severity: ServiceSeverity;
      summary: string;
    }) =>
      customerSheetReportsApi.update(v.id, {
        status: v.status,
        severity: v.severity,
        summary: v.summary,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['customer-sheet-reports'] });
      toast.success('리포트를 수정했습니다.');
      setEditing(null);
    },
    onError: (err) => toast.error(extractApiError(err), '리포트 수정 실패'),
  });

  // 소프트 삭제 — 행은 남고 목록에서만 사라진다. 잘못 지웠으면 서버의 restore 로
  // 되돌린다(화면 버튼은 없다).
  const deleteMutation = useMutation({
    mutationFn: (id: string) => customerSheetReportsApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['customer-sheet-reports'] });
      toast.success('리포트를 삭제했습니다.');
      setEditing(null);
    },
    onError: (err) => toast.error(extractApiError(err), '리포트 삭제 실패'),
  });

  // 하이라이트된 줄을 눈에 보이게 올린다. 목록이 길면 색만 칠해 봐야 안 보인다.
  const highlightRef = useRef<HTMLTableRowElement | null>(null);
  useEffect(() => {
    if (highlight && highlightRef.current) {
      highlightRef.current.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }, [highlight, items]);

  const applySearch = () =>
    setFilters((f) => ({ ...f, search: searchDraft.trim() || undefined, page: 1 }));

  return (
    <div style={fieldReportPage}>
      <FieldReportTabs />

      {/* 등급 카드 — 서버가 **필터와 무관하게** 전체를 센다. 카드는 "지금 무엇이
          쌓여 있나"를 보는 고정 지표라 필터마다 흔들리면 기준이 사라진다.
          누르면 해당 등급으로 목록만 좁힌다. */}
      <div
        style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}
        data-testid="sheet-report-severity-summary"
      >
        {SEVERITY_ORDER.map((sev) => {
          const tone = SEVERITY_TONE[sev];
          return (
            <StatFilterCard
              key={sev}
              icon={SEVERITY_ICON[sev]}
              color={tone.bar}
              activeBg={tone.bg}
              label={SERVICE_SEVERITY_LABELS[sev]}
              value={counts[sev] ?? 0}
              active={filters.severity === sev}
              onClick={() =>
                setFilters((f) => ({
                  ...f,
                  severity: f.severity === sev ? undefined : sev,
                  page: 1,
                }))
              }
            />
          );
        })}
      </div>

      {/* 필터 줄 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <select
          style={control}
          value={filters.status ?? (filters.open ? OPEN_OPTION : '')}
          onChange={(e) => {
            const v = e.target.value;
            setFilters((f) => ({
              ...f,
              open: v === OPEN_OPTION || undefined,
              status: v && v !== OPEN_OPTION ? (v as ServiceOrderStatus) : undefined,
              page: 1,
            }));
          }}
          aria-label="상태 필터"
        >
          <option value="">전체 상태</option>
          <option value={OPEN_OPTION}>미해결 (대기 · 진행중)</option>
          {SHEET_REPORT_STATUSES.map((k) => (
            <option key={k} value={k}>
              {SERVICE_ORDER_STATUS_LABELS[k]}
            </option>
          ))}
        </select>

        {/* 접수 달 · 반기(2026-10-28 오너 "월 · 반기 필터"). 반기는 달을 고른 뒤에 고른다. */}
        <select
          style={control}
          value={filters.month ?? ''}
          onChange={(e) =>
            setFilters((f) => ({
              ...f,
              month: e.target.value || undefined,
              half: e.target.value ? f.half : undefined,
              page: 1,
            }))
          }
          aria-label="월 필터"
        >
          <option value="">전체 월</option>
          {recentMonths(12).map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </select>
        <select
          style={control}
          value={filters.half ?? ''}
          disabled={!filters.month}
          onChange={(e) =>
            setFilters((f) => ({
              ...f,
              half: (e.target.value || undefined) as '1' | '2' | undefined,
              page: 1,
            }))
          }
          aria-label="반기 필터"
          title={filters.month ? undefined : '월을 먼저 고릅니다'}
        >
          <option value="">한 달 전체</option>
          <option value="1">상반기 (1~15일)</option>
          <option value="2">하반기 (16일~)</option>
        </select>

        <input
          value={searchDraft}
          onChange={(e) => setSearchDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') applySearch();
          }}
          placeholder="요약, 매장, 리포트 번호로 검색"
          aria-label="요약, 매장, 리포트 번호로 검색"
          style={{ ...control, width: 220 }}
        />
        <button type="button" style={{ ...control, cursor: 'pointer' }} onClick={applySearch}>
          검색
        </button>

        <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--text-secondary)' }}>
          총 {q.data?.total ?? 0} 건
        </span>
      </div>

      {/* 표 */}
      <div
        style={{
          border: '1px solid var(--border-default)',
          borderRadius: 'var(--r-card)',
          overflow: 'auto',
          background: 'var(--bg-surface)',
        }}
      >
        {/* minWidth 가 없으면 좁은 창에서 width:100% 가 열을 글자 단위로 쪼갠다.
            열이 9개라 이 아래로는 줄여도 못 읽는다 — 감싼 div 가 가로로 스크롤한다. */}
        <table style={{ width: '100%', minWidth: 1000, borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ ...th, width: 4, padding: 0 }} aria-hidden />
              <th style={th}>등급</th>
              <th style={th}>리포트 번호</th>
              <th style={th}>매장</th>
              <th style={th}>상태</th>
              {/* 2026-11-02 오너 — "조치 결과 말고 구분으로". 등급 옆의 문제 구분 칸은 뺐다
                  (고장 조치 이력의 구분 칸은 그대로). 내용은 시트 '조치결과'(자체 해결 · 필드팀 요청). */}
              <th style={th}>구분</th>
              <th style={{ ...th, width: '36%' }}>요약</th>
              <th style={th}>접수 시간</th>
              <th style={th}>사진</th>
              <th style={th}>영상</th>
              <th style={th}>편집</th>
            </tr>
          </thead>
          <tbody>
            {q.isLoading && (
              <tr>
                <td style={{ ...td, color: 'var(--text-secondary)' }} colSpan={11}>
                  불러오는 중…
                </td>
              </tr>
            )}

            {q.isError && (
              <tr>
                <td style={{ ...td, color: 'var(--fg-danger)' }} colSpan={11}>
                  {extractApiError(q.error)}
                </td>
              </tr>
            )}

            {!q.isLoading && !q.isError && items.length === 0 && (
              <tr>
                <td style={{ ...td, color: 'var(--text-secondary)' }} colSpan={11}>
                  아직 들어온 리포트가 없습니다. 현장앱 시트에 행이 추가되면 여기에 쌓입니다.
                </td>
              </tr>
            )}

            {items.map((r) => (
              <tr
                key={r.id}
                ref={r.sheetRowKey === highlight ? highlightRef : undefined}
                style={
                  r.sheetRowKey === highlight
                    ? { background: 'var(--bg-warning-subtle)', scrollMarginTop: 80 }
                    : undefined
                }
              >
                <td style={{ padding: 0, background: SEVERITY_TONE[r.severity].bar }} aria-hidden />
                <td style={td}>
                  <span
                    style={{
                      ...pill,
                      background: SEVERITY_TONE[r.severity].bg,
                      color: SEVERITY_TONE[r.severity].fg,
                    }}
                  >
                    {SERVICE_SEVERITY_LABELS[r.severity]}
                  </span>
                </td>
                <td style={{ ...td, whiteSpace: 'nowrap', fontWeight: 600 }}>
                  <ReportNoLink no={r.sheetRowKey} />
                </td>
                <td style={{ ...td, whiteSpace: 'nowrap' }}>
                  {r.storeName?.trim() ? r.storeName : '–'}
                </td>
                <td style={td}>
                  <span
                    style={{
                      ...pill,
                      ...(SHEET_STATUS_TONE[r.status] ?? SHEET_STATUS_TONE_UNKNOWN),
                    }}
                  >
                    {SERVICE_ORDER_STATUS_LABELS[r.status]}
                  </span>
                  {r.visit && <VisitTag visit={r.visit} />}
                </td>
                {/* 시트 '조치결과'(머리글은 '구분'). '현장 해결' 은 콘솔 어휘인 '자체 해결' 로 보여
                    주고, 정본에 없는 값은 시트 원문 그대로 둔다 — 뭉개면 시트와
                    대조할 수가 없다. 배지를 안 쓰는 건 등급·상태 배지 옆에서
                    세 번째 색이 되면 어느 게 급한 신호인지 흐려지기 때문이다. */}
                <td style={{ ...td, whiteSpace: 'nowrap', color: 'var(--fg-muted)' }}>
                  {sheetOutcomeLabel(r.sheetOutcome) ?? '–'}
                </td>
                <td style={multiline} data-testid="sheet-report-summary">
                  {r.summary}
                </td>
                <td style={{ ...td, whiteSpace: 'nowrap' }}>{fmtKst(r.receivedAt)}</td>
                <td style={td}>
                  <MediaLink url={r.photoUrl} label="사진 링크" />
                </td>
                <td style={td}>
                  <MediaLink url={r.videoUrl} label="영상 링크" />
                </td>
                <td style={td}>
                  <button
                    type="button"
                    onClick={() => setEditing(r)}
                    disabled={!canEdit}
                    title={canEdit ? '상태와 등급 수정' : '수정 권한이 없습니다'}
                    aria-label="리포트 수정"
                    data-testid="sheet-report-edit"
                    style={{
                      background: 'transparent',
                      border: 'none',
                      cursor: canEdit ? 'pointer' : 'default',
                      color: canEdit ? 'var(--fg-muted)' : 'var(--border-default)',
                      padding: 2,
                      lineHeight: 0,
                    }}
                  >
                    <Icon name="pen" size={14} aria-hidden />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <EditDialog
          // 리포트마다 창을 새로 만든다. key 가 없으면 React 가 같은 인스턴스를
          // 재사용해 **확인 단계(confirming)와 요약 초안이 다음 리포트로 넘어간다** —
          // 앞 건에서 '삭제'를 눌러 둔 채 다른 건을 열면 첫 클릭에 바로 지워진다.
          key={editing.id}
          report={editing}
          busy={updateMutation.isPending}
          deleting={deleteMutation.isPending}
          canDelete={canEdit}
          onCancel={() => setEditing(null)}
          onSave={(status, severity, summary) =>
            updateMutation.mutate({ id: editing.id, status, severity, summary })
          }
          onDelete={() => deleteMutation.mutate(editing.id)}
          onMerge={
            canEdit
              ? () => {
                  setMerging(editing);
                  setEditing(null);
                }
              : undefined
          }
        />
      )}
      {merging && <MergeReportPicker source={merging} onClose={() => setMerging(null)} />}
    </div>
  );
}

/**
 * 리포트 번호 → 그 신고를 다룬 **고장 조치 이력**으로.
 *
 * 주소에 번호를 실어 보낸다(상태로 넘기지 않는다) — 링크를 복사해 남에게 줄 수
 * 있고, 뒤로가기가 원래 화면으로 되돌린다.
 */
/**
 * 방문 일정이 이어진 리포트 — 캘린더 유지보수가 만든 것이든, 기존 리포트에 이은 것이든(2026-11-03).
 * 상태 배지 밑에 작게 붙인다. 네 번째 색을 더하지 않으려고 글자만 쓴다.
 */
function VisitTag({ visit }: { visit: NonNullable<CustomerSheetReportDto['visit']> }) {
  const md = visitMonthDay(visit);
  return (
    <div
      style={{ marginTop: 4, fontSize: 11, color: 'var(--fg-muted)', whiteSpace: 'nowrap' }}
      title={visitLabel(visit)}
      data-testid="sheet-report-visit"
    >
      {md ? `방문 ${md}` : '방문 일정'}
    </div>
  );
}

function ReportNoLink({ no }: { no: string }) {
  if (!no?.trim()) return <span style={{ color: 'var(--text-secondary)' }}>–</span>;
  return (
    <Link
      // 그 번호의 조치만 걸러 연다(2026-10-28 오너 "번호로 조치 이력 찾기") — 종전에는 한 쪽 안에 있을 때만 칠했다.
      to={`/field/fault-remediations?reportNo=${encodeURIComponent(no)}&highlight=${encodeURIComponent(no)}`}
      style={{ color: 'var(--fg-brand)', textDecoration: 'none' }}
      title="이 신고의 조치 이력 보기"
    >
      {no}
    </Link>
  );
}

/**
 * 사진·영상 — **시트가 준 링크를 그대로 연다**(콘솔로 파일을 복사하지 않는다,
 * 오너 결정). 외부 링크라 noopener·noreferrer 를 건다.
 *
 * 스킴 검증은 수집 단(IngestCustomerSheetReportDto)에서 http/https 만 통과시킨다 —
 * `javascript:` 같은 값이 여기까지 오지 않는다.
 */
function MediaLink({ url, label }: { url: string | null; label: string }) {
  if (!url) return <span style={{ color: 'var(--text-secondary)' }}>–</span>;
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      style={{ color: 'var(--fg-brand)', fontWeight: 600, whiteSpace: 'nowrap' }}
    >
      {label}
    </a>
  );
}

function EditDialog({
  report,
  busy,
  deleting,
  canDelete,
  onCancel,
  onSave,
  onDelete,
  onMerge,
}: {
  report: CustomerSheetReportDto;
  busy: boolean;
  deleting: boolean;
  canDelete: boolean;
  onCancel: () => void;
  onSave: (status: ServiceOrderStatus, severity: ServiceSeverity, summary: string) => void;
  onDelete: () => void;
  /** 같은 건인 다른 리포트와 합친다 — 먼저 등록된 번호가 남는다. */
  onMerge?: () => void;
}) {
  const [status, setStatus] = useState<ServiceOrderStatus>(report.status);
  const [severity, setSeverity] = useState<ServiceSeverity>(report.severity);
  const [summary, setSummary] = useState(report.summary);
  // 삭제는 한 번 더 묻는다. 브라우저 confirm 을 쓰지 않는 이유 — 이 창 안에서
  // 무엇을 지우는지(요약)를 같이 보여줘야 잘못 누른 걸 알아챌 수 있다.
  const [confirming, setConfirming] = useState(false);

  // 빈 요약은 저장하지 않는다 — 목록에서 무슨 건인지 알 수 없게 된다.
  const summaryOk = summary.trim().length > 0;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="리포트 수정"
      // 배경을 눌러 닫기 — 누른 곳과 뗀 곳이 **둘 다 배경**일 때만 닫는다.
      // 입력칸에서 글자를 드래그하다 배경에서 손을 떼도 닫히던 사고를 막는다.
      {...useBackdropDismiss(onCancel)}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onCancel();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.35)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
      }}
    >
      {/* 안쪽 패널에 stopPropagation 이 필요 없다 — 배경 판정이
          `e.target === e.currentTarget` 이라 여기서 난 이벤트는 애초에 안 걸린다. */}
      <div
        style={{
          background: 'var(--bg-surface)',
          borderRadius: 'var(--r-card)',
          padding: 20,
          width: 360,
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
        }}
      >
        <h2 style={{ margin: 0, fontSize: 15 }}>리포트 수정</h2>

        <label style={{ fontSize: 12, fontWeight: 600 }}>
          요약
          <textarea
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
            rows={4}
            maxLength={2000}
            aria-label="요약"
            data-testid="sheet-report-summary-input"
            style={{
              ...control,
              width: '100%',
              marginTop: 4,
              resize: 'vertical',
              fontFamily: 'inherit',
              lineHeight: 1.5,
            }}
          />
        </label>
        {!summaryOk && (
          <span style={{ fontSize: 11, color: 'var(--fg-danger)' }}>요약은 비울 수 없습니다.</span>
        )}

        <label style={{ fontSize: 12, fontWeight: 600 }}>
          상태
          <select
            style={{ ...control, width: '100%', marginTop: 4 }}
            value={status}
            onChange={(e) => setStatus(e.target.value as ServiceOrderStatus)}
          >
            {statusOptionsFor(report.status).map((k) => (
              <option key={k} value={k}>
                {SERVICE_ORDER_STATUS_LABELS[k]}
              </option>
            ))}
          </select>
        </label>

        <label style={{ fontSize: 12, fontWeight: 600 }}>
          등급
          <select
            style={{ ...control, width: '100%', marginTop: 4 }}
            value={severity}
            onChange={(e) => setSeverity(e.target.value as ServiceSeverity)}
          >
            {Object.entries(SERVICE_SEVERITY_LABELS).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </label>

        {/* 2026-11-03 시트 양방향 연동 — 어디까지 시트로 가는지 알려 둔다. 등급은 시트에 칸이 없다. */}
        <p
          style={{ margin: 0, fontSize: 11, color: 'var(--fg-muted)', lineHeight: 1.5 }}
          data-testid="sheet-report-sync-hint"
        >
          요약과 상태는 1분 안에 시트의 &lsquo;작성 내용&rsquo;과 &lsquo;콘솔상태&rsquo;에도
          반영됩니다. 등급은 시트에 칸이 없어 콘솔에만 남습니다.
        </p>

        {onMerge && !confirming && (
          <div
            style={{
              borderTop: '1px solid var(--border-subtle)',
              paddingTop: 10,
              display: 'flex',
              flexDirection: 'column',
              gap: 6,
              fontSize: 12,
              color: 'var(--fg-muted)',
              lineHeight: 1.5,
            }}
          >
            같은 신고가 리포트 두 개로 올라왔으면 합쳐서 번호를 하나로 둡니다. 먼저 등록된 번호가
            남습니다.
            <button
              type="button"
              onClick={onMerge}
              data-testid="sheet-report-merge"
              style={{ ...control, alignSelf: 'flex-start', cursor: 'pointer', fontWeight: 600 }}
            >
              같은 건으로 합치기
            </button>
          </div>
        )}

        {confirming && (
          <div
            style={{
              border: '1px solid var(--border-danger-saturated)',
              background: 'var(--bg-danger-subtle)',
              color: 'var(--fg-on-danger-tint)',
              borderRadius: 'var(--r-card)',
              padding: '8px 10px',
              fontSize: 12,
              lineHeight: 1.5,
            }}
            role="alert"
          >
            <b>{report.summary}</b>
            <br />이 리포트를 목록에서 지웁니다. 시트 원본은 그대로 남습니다.
          </div>
        )}

        <div
          style={{
            display: 'flex',
            gap: 8,
            alignItems: 'center',
            justifyContent: 'flex-end',
            marginTop: 4,
          }}
        >
          {canDelete && (
            <button
              type="button"
              data-testid="sheet-report-delete"
              disabled={deleting}
              onClick={() => (confirming ? onDelete() : setConfirming(true))}
              style={{
                ...control,
                marginRight: 'auto', // 저장과 멀리 떨어뜨린다 — 오클릭 방지
                cursor: deleting ? 'default' : 'pointer',
                background: confirming ? 'var(--fg-danger)' : 'transparent',
                color: confirming ? 'var(--bg-surface)' : 'var(--fg-danger)',
                borderColor: 'var(--fg-danger)',
                fontWeight: 600,
                opacity: deleting ? 0.6 : 1,
              }}
            >
              {deleting ? '삭제 중…' : confirming ? '삭제 확인' : '삭제'}
            </button>
          )}
          <button
            type="button"
            style={{ ...control, cursor: 'pointer' }}
            onClick={() => (confirming ? setConfirming(false) : onCancel())}
          >
            취소
          </button>
          <button
            type="button"
            disabled={busy || !summaryOk}
            onClick={() => onSave(status, severity, summary.trim())}
            style={{
              ...control,
              cursor: busy || !summaryOk ? 'default' : 'pointer',
              background: 'var(--fg-brand)',
              color: 'var(--bg-surface)',
              borderColor: 'var(--fg-brand)',
              opacity: busy || !summaryOk ? 0.6 : 1,
            }}
          >
            {busy ? '저장 중…' : '저장'}
          </button>
        </div>
      </div>
    </div>
  );
}
